import React, { useCallback, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { expandHalfHourStarts } from '../lib/slots';
import { motion } from 'framer-motion';
import { Calendar } from '../components/ui/calendar';
import { es as esLocale, enGB as enLocale, de as deLocale } from 'date-fns/locale';
import { Clock, Calendar as CalendarIcon, Loader2, Check, Ticket, AlertCircle } from 'lucide-react';
import { Navigation } from '../components/Navigation';
import { Footer } from '../components/Footer';
import { useLanguage } from '../context/LanguageContext';
import { formatMinutes } from '../lib/time';
import Seo from '../components/Seo';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PACK_NAMES = { pack5: 'Pack 5 clases', pack10: 'Pack 10 clases' };

export default function MiPackPage() {
  const { token } = useParams();
  const { language } = useLanguage();
  const [pack, setPack] = useState(null);
  const [availability, setAvailability] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedTime, setSelectedTime] = useState(null);
  const [duration, setDuration] = useState(60);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [done, setDone] = useState('');

  const load = useCallback(async () => {
    try {
      const [p, a] = await Promise.all([
        axios.get(`${API}/packs/by-token/${token}`),
        axios.get(`${API}/availability`),
      ]);
      setPack(p.data);
      if (p.data.allow_half_hour && !p.data.can_book_60) setDuration(30);
      setAvailability(a.data);
    } catch (e) {
      setError(e.response?.data?.detail || 'No hemos podido cargar tu pack.');
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { load(); }, [load]);

  const formatDate = (date) => {
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, '0');
    const d = String(date.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
  };

  const availableTimes = React.useMemo(() => {
    if (!selectedDate || !availability) return [];
    const dayCfg = availability.weekly?.[String(selectedDate.getDay())];
    const dateStr = formatDate(selectedDate);
    const opened = availability.opened_slots?.[dateStr] || [];
    // Con 30 minutos solo hace falta que esté libre su propia media hora;
    // con 60 minutos debe estar libre toda la hora siguiente.
    const occupied = duration === 30
      ? (availability.booked_cells?.[dateStr] || [])
      : (availability.booked_slots?.[dateStr] || []);
    const taken = new Set([...occupied, ...(availability.blocked_slots?.[dateStr] || [])]);
    const base = dayCfg && dayCfg.enabled ? (dayCfg.slots || []) : [];
    return expandHalfHourStarts([...base, ...opened]).filter((s) => !taken.has(s));
  }, [selectedDate, availability, duration]);

  const isDateDisabled = (date) => {
    if (date < new Date(new Date().setHours(0, 0, 0, 0))) return true;
    if (!availability) return true;
    const dateStr = formatDate(date);
    if ((availability.blocked_dates || []).includes(dateStr)) return true;
    const dayCfg = availability.weekly?.[String(date.getDay())];
    const weeklyEnabled = dayCfg && dayCfg.enabled && (dayCfg.slots || []).length > 0;
    const opened = (availability.opened_slots?.[dateStr] || []).length > 0;
    return !weeklyEnabled && !opened;
  };

  const bookSession = async () => {
    if (!selectedDate || !selectedTime) return;
    setSaving(true);
    setError('');
    setDone('');
    try {
      await axios.post(`${API}/packs/by-token/${token}/bookings`, {
        date: formatDate(selectedDate),
        time: selectedTime,
        duration,
      });
      setDone(`¡Clase reservada para el ${formatDate(selectedDate)} a las ${selectedTime} (${duration} minutos)! Te he enviado la confirmación por email.`);
      setSelectedDate(null);
      setSelectedTime(null);
      await load();
    } catch (e) {
      setError(e.response?.data?.detail || 'No hemos podido reservar esa hora. Prueba con otra.');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#E8F5FA]">
        <Seo path={`/mi-pack/${token}`} title="Mi pack de clases" noindex />
        <Loader2 className="w-10 h-10 text-[#7DBDD9] animate-spin" />
      </div>
    );
  }

  if (!pack) {
    return (
      <div className="min-h-screen bg-white">
        <Seo path={`/mi-pack/${token}`} title="Mi pack de clases" noindex />
        <Navigation />
        <div className="pt-36 pb-24 px-6 text-center" data-testid="pack-not-found">
          <AlertCircle className="w-12 h-12 text-[#E38987] mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-[#4A4A4A] mb-2">Enlace no válido</h1>
          <p className="text-gray-600">{error || 'Revisa el enlace que te envié por email.'}</p>
        </div>
        <Footer />
      </div>
    );
  }

  const isActive = pack.status === 'active';
  // Para los bonos con clases de 30 min la fuente de verdad es el saldo de
  // tiempo, nunca el número de clases.
  const canBook = pack.allow_half_hour ? pack.can_book_30 : pack.can_book_60;

  return (
    <div className="min-h-screen bg-white">
      <Seo path={`/mi-pack/${token}`} title="Mi pack de clases" noindex />
      <Navigation />

      <section className="pt-32 pb-12 bg-gradient-to-b from-[#E8F5FA] to-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6">
          <span className="text-[#F5A6A4] font-semibold text-sm uppercase tracking-wider">Mi pack de clases</span>
          <h1 className="text-4xl sm:text-5xl font-bold text-[#4A4A4A] mt-3 mb-6" data-testid="pack-title">
            Hola {pack.name}
          </h1>

          <div className="grid sm:grid-cols-3 gap-4">
            <div className="p-5 rounded-2xl bg-white shadow-lg border border-gray-100">
              <p className="text-xs font-bold uppercase tracking-wider text-gray-400 mb-1">Pack</p>
              <p className="text-lg font-bold text-[#4A4A4A] flex items-center gap-2">
                <Ticket className="w-5 h-5 text-[#7DBDD9]" />
                {PACK_NAMES[pack.pack_type] || pack.pack_type}
              </p>
            </div>
            <div className="p-5 rounded-2xl bg-gradient-to-r from-[#A8D8EA] to-[#7DBDD9] text-white shadow-lg" data-testid="pack-remaining">
              <p className="text-xs font-bold uppercase tracking-wider text-white/80 mb-1">
                {pack.allow_half_hour ? 'Tiempo disponible' : 'Clases disponibles'}
              </p>
              {pack.allow_half_hour ? (
                <p className="text-3xl font-bold" data-testid="pack-remaining-minutes">
                  {formatMinutes(pack.remaining_minutes)}
                  <span className="text-base font-semibold text-white/80"> / {formatMinutes(pack.total_minutes)}</span>
                </p>
              ) : (
                <p className="text-3xl font-bold">{pack.remaining_sessions}<span className="text-base font-semibold text-white/80"> / {pack.total_sessions}</span></p>
              )}
            </div>
            <div className="p-5 rounded-2xl bg-white shadow-lg border border-gray-100">
              <p className="text-xs font-bold uppercase tracking-wider text-gray-400 mb-1">Estado</p>
              <p className={`text-lg font-bold ${isActive ? 'text-[#6BA368]' : 'text-[#D9903A]'}`} data-testid="pack-status">
                {isActive ? 'Activo' : pack.status === 'cancelled' ? 'Cancelado' : 'Pendiente de confirmación'}
              </p>
            </div>
          </div>

          {pack.allow_half_hour && (
            <div className="mt-4 grid sm:grid-cols-4 gap-3" data-testid="pack-minutes-breakdown">
              {[
                ['Saldo total', pack.total_minutes, 'pack-total-minutes'],
                ['Consumido', pack.completed_minutes, 'pack-completed-minutes'],
                ['Programado', pack.scheduled_minutes, 'pack-scheduled-minutes'],
                ['Disponible para reservar', pack.remaining_minutes, 'pack-available-minutes'],
              ].map(([label, value, tid]) => (
                <div key={tid} className="px-4 py-3 rounded-xl bg-white border border-gray-100 shadow-sm">
                  <p className="text-[11px] font-bold uppercase tracking-wider text-gray-400">{label}</p>
                  <p className="text-lg font-bold text-[#4A4A4A]" data-testid={tid}>{formatMinutes(value)}</p>
                </div>
              ))}
            </div>
          )}

        </div>
      </section>

      <section className="pb-20">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 grid lg:grid-cols-2 gap-8">
          {/* Reserved classes */}
          <div>
            <h2 className="text-base md:text-lg font-bold text-[#4A4A4A] uppercase tracking-wider mb-4">Tus clases reservadas</h2>
            {pack.sessions.length === 0 ? (
              <p className="text-gray-500 text-sm">Todavía no tienes clases reservadas.</p>
            ) : (
              <ul className="space-y-2" data-testid="pack-session-list">
                {pack.sessions.map((s, i) => (
                  <li key={s.id} data-testid={`pack-booked-${i}`} className="flex items-center gap-3 px-4 py-3 rounded-xl bg-[#E8F5FA]">
                    <Check className="w-4 h-4 text-[#7DBDD9] flex-shrink-0" />
                    <span className="text-sm font-semibold text-[#4A4A4A]">
                      {s.date} · {s.time} <span className="text-gray-500 font-medium">({s.duration_minutes || 60} min)</span>
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </div>

          {/* Book another class */}
          <div>
            <h2 className="text-base md:text-lg font-bold text-[#4A4A4A] uppercase tracking-wider mb-4">Reservar otra clase</h2>

            {!isActive && (
              <div className="p-5 rounded-2xl bg-[#FFF0EF] border border-[#F5A6A4]/40" data-testid="pack-pending-note">
                <p className="text-sm text-[#4A4A4A]">
                  Tu pack está <strong>pendiente de confirmación</strong>. En cuanto confirme el pago podrás reservar aquí el resto de tus clases.
                </p>
              </div>
            )}

            {isActive && !canBook && (
              <div className="p-5 rounded-2xl bg-[#E8F5FA]" data-testid="pack-complete-note">
                <p className="text-sm text-[#4A4A4A]">Ya has reservado todo el tiempo de tu bono. ¡Nos vemos en clase!</p>
              </div>
            )}

            {isActive && canBook && (
              <div className="space-y-4">
                {pack.allow_half_hour && (
                  <div className="bg-white p-5 rounded-2xl shadow-lg border border-gray-100" data-testid="pack-duration-picker">
                    <div className="flex items-center space-x-2 mb-3">
                      <Clock className="w-5 h-5 text-[#A8D8EA]" />
                      <h3 className="text-base font-bold text-[#4A4A4A]">Elige la duración</h3>
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                      {[30, 60].map((mins) => (
                        <button
                          key={mins}
                          onClick={() => { setDuration(mins); setSelectedTime(null); }}
                          disabled={pack.remaining_minutes < mins}
                          data-testid={`pack-duration-${mins}`}
                          className={`py-2.5 px-3 rounded-xl font-semibold text-sm transition-all disabled:opacity-40 ${
                            duration === mins
                              ? 'bg-[#A8D8EA] text-white shadow-md'
                              : 'bg-gray-50 text-gray-700 hover:bg-gray-100 border border-gray-200'
                          }`}
                        >
                          {mins} minutos
                        </button>
                      ))}
                    </div>
                    <p className="text-xs text-gray-500 mt-3">
                      Se descontarán {duration} minutos de tu bono · te quedan {formatMinutes(pack.remaining_minutes)}.
                    </p>
                  </div>
                )}
                <div className="bg-white p-5 rounded-2xl shadow-lg border border-gray-100">
                  <div className="flex items-center space-x-2 mb-3">
                    <CalendarIcon className="w-5 h-5 text-[#A8D8EA]" />
                    <h3 className="text-base font-bold text-[#4A4A4A]">Elige la fecha</h3>
                  </div>
                  <Calendar
                    mode="single"
                    selected={selectedDate}
                    onSelect={(d) => { setSelectedDate(d); setSelectedTime(null); }}
                    disabled={isDateDisabled}
                    locale={language === 'de' ? deLocale : language === 'en' ? enLocale : esLocale}
                    className="rounded-md border-0"
                  />
                </div>

                {selectedDate && (
                  <motion.div
                    initial={{ opacity: 0, y: 8 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="bg-white p-5 rounded-2xl shadow-lg border border-gray-100"
                  >
                    <div className="flex items-center space-x-2 mb-3">
                      <Clock className="w-5 h-5 text-[#A8D8EA]" />
                      <h3 className="text-base font-bold text-[#4A4A4A]">Elige la hora</h3>
                    </div>
                    <div className="grid grid-cols-3 gap-2">
                      {availableTimes.map((time) => (
                        <button
                          key={time}
                          onClick={() => setSelectedTime(time)}
                          data-testid={`pack-time-${time}`}
                          className={`py-2.5 px-3 rounded-xl font-semibold text-sm transition-all ${
                            selectedTime === time
                              ? 'bg-[#A8D8EA] text-white shadow-md'
                              : 'bg-gray-50 text-gray-700 hover:bg-gray-100 border border-gray-200'
                          }`}
                        >
                          {time}
                        </button>
                      ))}
                    </div>
                    {availableTimes.length === 0 && (
                      <p className="text-gray-500 text-sm text-center py-2">No quedan horas libres ese día.</p>
                    )}
                  </motion.div>
                )}

                {error && <p className="text-sm text-red-500 font-semibold" data-testid="pack-error">{error}</p>}
                {done && <p className="text-sm text-[#6BA368] font-semibold" data-testid="pack-done">{done}</p>}

                <button
                  onClick={bookSession}
                  disabled={saving || !selectedDate || !selectedTime}
                  data-testid="pack-book-btn"
                  className="w-full py-3.5 bg-gradient-to-r from-[#A8D8EA] to-[#7DBDD9] text-white rounded-xl font-bold shadow-lg hover:shadow-xl transition-all disabled:opacity-50 flex items-center justify-center gap-2"
                >
                  {saving ? <Loader2 className="w-5 h-5 animate-spin" /> : <Check className="w-5 h-5" />}
                  {pack.allow_half_hour ? `Reservar clase de ${duration} min` : 'Reservar esta clase'}
                </button>
              </div>
            )}
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
